import {
  ArrowClockwise,
  CaretDown,
  CaretRight,
  Info,
  Link,
  Warning,
} from '@phosphor-icons/react';
import type {
  AccountAnalyticsDto,
  AccountStatsStatus,
  AnalyticsLastReelDto,
} from '@reelplicate/api-client/model';
import {
  Badge,
  Button,
  cn,
  Inline,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@reelplicate/ui';
import { useState } from 'react';

import type { Account, AccountGroup } from '@/features/calendar/calendar-model';
import { PlatformDot } from '@/features/calendar/platform-stack';
import { PLATFORM_NAMES } from '@/features/calendar/platforms';

import { formatAgo, formatCount, formatGain, formatPercent, formatWhen } from './format';
import { PostActivity, PostActivityDays, PostActivityLegend } from './post-activity';

type SortKey = 'followers' | 'viewsGained' | 'likesGained' | 'engagement';

const SORTABLE: { key: SortKey; label: string; width: number }[] = [
  { key: 'followers', label: 'Followers', width: 80 },
  { key: 'viewsGained', label: 'Views gained', width: 114 },
  { key: 'likesGained', label: 'Likes gained', width: 92 },
  { key: 'engagement', label: 'Engagement', width: 92 },
];
const COLUMN_COUNT = 8;

type AccountsTableProps = {
  groups: AccountGroup[];
  accounts: Account[];
  /** By account id; accounts without a tracked row point to Connected accounts. */
  stats: Map<string, AccountAnalyticsDto>;
  /** Today's place in the week (0 = Monday). */
  todayIndex: number;
  now: Date;
  onConnect: (account: Account) => void;
  /** How each account's last read went, by id, for the untracked rows. */
  readStates?: Map<string, ReadState>;
  /** Read an account again now ("Check again"). */
  onCheck?: (account: Account) => void;
  /** The account being checked, while it is. */
  checking?: string;
};

export type ReadState = { statsStatus: AccountStatsStatus; statsError: string | null };

/**
 * Figma "By account" (171:240): every account's gains, grouped by brand /
 * persona, with this week's post activity and its last reel. Numeric
 * columns sort within each group; accounts that aren't tracked yet point to
 * Connected accounts instead.
 */
export function AccountsTable({
  groups: allGroups,
  accounts,
  stats,
  todayIndex,
  now,
  onConnect,
  readStates,
  onCheck,
  checking,
}: AccountsTableProps) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'descending' | 'ascending' }>({
    key: 'viewsGained',
    dir: 'descending',
  });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const tracked = (a: Account) => {
    const row = stats.get(a.id);
    return row?.tracked ? row : undefined;
  };

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'descending' ? 'ascending' : 'descending' }
        : { key, dir: 'descending' },
    );

  const byStat = (a: Account, b: Account) => {
    const [x, y] = [tracked(a), tracked(b)];
    if (!x || !y) return x ? -1 : y ? 1 : 0; // untracked last
    const diff = x[sort.key] - y[sort.key];
    return sort.dir === 'descending' ? -diff : diff;
  };

  const groups = allGroups
    .map((group) => ({
      group,
      accounts: accounts.filter((a) => a.groupId === group.id).sort(byStat),
    }))
    .filter((g) => g.accounts.length > 0);

  return (
    <section
      aria-labelledby="by-account"
      className="bg-bg-surface border-border-subtle w-full overflow-hidden rounded-2xl border"
    >
      <div className="flex flex-wrap items-center gap-4 px-6 py-[18px]">
        <h2 id="by-account" className="text-heading-h4 text-text-primary flex-1">
          By account
        </h2>
        <PostActivityLegend />
        <span aria-hidden className="bg-border-subtle h-4 w-px" />
        <span className="text-caption text-text-tertiary">
          {[...stats.values()].some((s) => s.source === 'PUBLIC')
            ? 'Synced every 6 hours'
            : 'Synced hourly'}
        </span>
      </div>

      <Table fixed aria-labelledby="by-account" className="min-w-[1100px]">
        <TableHeader>
          <TableRow>
            <TableHead className="h-auto py-2.5 pr-2 pl-6 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]">
              Account
            </TableHead>
            {SORTABLE.map(({ key, label, width }) => (
              <TableHead
                key={key}
                align="right"
                width={width}
                sort={sort.key === key ? sort.dir : 'none'}
                onSort={() => toggleSort(key)}
                className="h-auto py-2.5 pr-2 pl-0 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]"
              >
                {label}
              </TableHead>
            ))}
            <TableHead
              width={158}
              className="h-auto py-2.5 pr-0 pl-4 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]"
            >
              <span className="flex flex-col gap-1">
                Post activity · this week
                <PostActivityDays todayIndex={todayIndex} />
              </span>
            </TableHead>
            <TableHead
              width={196}
              className="h-auto py-2.5 pr-0 pl-3 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]"
            >
              Last reel
            </TableHead>
            <TableHead
              width={104}
              className="h-auto py-2.5 pr-6 pl-2 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]"
            >
              Last synced
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map(({ group, accounts: rows }) => {
            const open = !collapsed.has(group.id);
            const rowStats = rows.flatMap((a) => tracked(a) ?? []);
            const views = rowStats.reduce((sum, r) => sum + r.viewsGained, 0);
            const interactions = rowStats.reduce(
              (sum, r) => sum + r.interactionsGained,
              0,
            );
            const engagement = views > 0 ? (interactions / views) * 100 : 0;
            return [
              <TableRow key={group.id} className="bg-bg-subtle hover:bg-bg-subtle">
                <td
                  colSpan={COLUMN_COUNT}
                  className="px-6 py-2 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]"
                >
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() =>
                        setCollapsed((prev) => {
                          const next = new Set(prev);
                          if (open) next.add(group.id);
                          else next.delete(group.id);
                          return next;
                        })
                      }
                      className="text-label-md text-text-primary focus-visible:shadow-focus-ring inline-flex items-center gap-2 rounded-xs outline-none"
                    >
                      {open ? (
                        <CaretDown aria-hidden className="text-icon-secondary size-4" />
                      ) : (
                        <CaretRight aria-hidden className="text-icon-secondary size-4" />
                      )}
                      {group.name}
                    </button>
                    <Badge size="sm" tone={group.kind === 'brand' ? 'brand' : 'neutral'}>
                      {group.kind === 'brand' ? 'Brand' : 'Persona'}
                    </Badge>
                    <span className="text-caption text-text-tertiary flex-1">
                      {rows.length} {rows.length === 1 ? 'account' : 'accounts'}
                    </span>
                    {rowStats.length > 0 && (
                      <span className="text-caption text-text-secondary">
                        {formatGain(views)} views · {formatPercent(engagement)} engagement
                      </span>
                    )}
                  </div>
                </td>
              </TableRow>,
              ...(open
                ? rows.map((account) => {
                    const row = tracked(account);
                    return row ? (
                      <AccountRow
                        key={account.id}
                        account={account}
                        stats={row}
                        todayIndex={todayIndex}
                        now={now}
                      />
                    ) : (
                      <UntrackedRow
                        key={account.id}
                        account={account}
                        state={readStates?.get(account.id)}
                        checking={checking === account.id}
                        onConnect={() => onConnect(account)}
                        onCheck={onCheck && (() => onCheck(account))}
                      />
                    );
                  })
                : []),
            ];
          })}
        </TableBody>
      </Table>
    </section>
  );
}

/** The account cell: platform mark, platform name over the handle. */
function AccountCell({
  account,
  source,
  className,
}: {
  account: Account;
  /** Where its numbers came from; PUBLIC gets a badge. */
  source?: AccountAnalyticsDto['source'];
  className?: string;
}) {
  return (
    <TableCell className="h-auto py-3 pr-2 pl-6 shadow-[inset_0_1px_0_0_var(--color-border-subtle)]">
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        <PlatformDot platform={account.platform} size="lg" />
        <div className="flex min-w-0 flex-col">
          <span className="text-label-md text-text-primary flex items-center gap-1.5 leading-5">
            {PLATFORM_NAMES[account.platform]}
            {source === 'PUBLIC' && (
              <Badge
                size="sm"
                tone="info"
                title="Read from public data by handle. Before it was added, each post's numbers count on the day it went out."
              >
                Public
              </Badge>
            )}
          </span>
          <span className="text-caption text-text-secondary truncate">
            {account.handle}
          </span>
        </div>
      </div>
    </TableCell>
  );
}

const rowLine = 'shadow-[inset_0_1px_0_0_var(--color-border-subtle)]';

/**
 * A right-aligned figure in Body/Small (13px, Figma), not the ui numeric
 * cell's Data/Medium. Figma rows are 24px in from the card edge with 8px
 * right padding on these columns, so the default cell padding is overridden.
 */
function NumberCell({ strong, children }: { strong?: boolean; children: string }) {
  return (
    <TableCell className={cn('h-auto py-3 pr-2 pl-0', rowLine)}>
      <span
        className={cn(
          'text-text-primary w-full truncate text-right tabular-nums',
          strong ? 'text-body-sm-strong' : 'text-body-sm',
        )}
      >
        {children}
      </span>
    </TableCell>
  );
}

function AccountRow({
  account,
  stats,
  todayIndex,
  now,
}: {
  account: Account;
  stats: AccountAnalyticsDto;
  todayIndex: number;
  now: Date;
}) {
  return (
    <TableRow>
      <AccountCell account={account} source={stats.source} />
      <NumberCell>{formatCount(stats.followers)}</NumberCell>
      <NumberCell strong>{formatGain(stats.viewsGained, 1_000)}</NumberCell>
      <NumberCell>{formatGain(stats.likesGained, 1_000)}</NumberCell>
      <NumberCell>{formatPercent(stats.engagement)}</NumberCell>
      <TableCell className={cn('h-auto py-3 pr-0 pl-4', rowLine)}>
        <PostActivity activity={stats.activity} todayIndex={todayIndex} />
      </TableCell>
      <TableCell className={cn('h-auto py-3 pr-0 pl-3', rowLine)}>
        {stats.lastReel ? (
          <LastReel reel={stats.lastReel} platform={account.platform} />
        ) : (
          <span className="text-body-sm text-text-tertiary">No reels yet</span>
        )}
      </TableCell>
      <TableCell
        className={cn('text-body-sm text-text-secondary h-auto py-3 pr-6 pl-2', rowLine)}
      >
        {stats.lastSyncedAt ? formatAgo(new Date(stats.lastSyncedAt), now) : '—'}
      </TableCell>
    </TableRow>
  );
}

/**
 * Figma "Row · not tracked" (171:540). An account the last read couldn't
 * find or read says so, with the reason, and offers to check again instead
 * of promising numbers after the next sync.
 */
function UntrackedRow({
  account,
  state,
  checking,
  onConnect,
  onCheck,
}: {
  account: Account;
  state?: ReadState;
  checking: boolean;
  onConnect: () => void;
  onCheck?: () => void;
}) {
  const platform = PLATFORM_NAMES[account.platform];
  // Retryable failures keep the account pending but say why.
  const failed =
    onCheck &&
    state &&
    (state.statsStatus === 'NOT_FOUND' ||
      state.statsStatus === 'ERROR' ||
      Boolean(state.statsError));
  return (
    <TableRow>
      <AccountCell account={account} className="opacity-60" />
      <td colSpan={COLUMN_COUNT - 1} className={cn('py-3 pr-6', rowLine)}>
        <div className="bg-bg-subtle flex items-center gap-3 rounded-[10px] px-4 py-2.5">
          {failed ? (
            <Warning aria-hidden className="text-status-warning-fg size-4 shrink-0" />
          ) : (
            <Info aria-hidden className="text-icon-secondary size-4 shrink-0" />
          )}
          <p className="text-body-sm text-text-secondary flex-1">
            {failed
              ? state.statsStatus === 'NOT_FOUND'
                ? `${platform} didn’t find ${account.handle} on the last read.`
                : state.statsStatus === 'ERROR'
                  ? `${platform} couldn’t be read for ${account.handle}${state.statsError ? `: ${state.statsError}` : '.'}`
                  : `${platform} couldn’t be read for ${account.handle} on the last try - it’s retried every hour: ${state.statsError}`
              : `Not tracked yet — numbers for ${account.handle} show up after its next ${platform} sync.`}
          </p>
          {failed ? (
            <Button
              size="sm"
              variant="outline"
              leadingIcon={ArrowClockwise}
              disabled={checking}
              onClick={onCheck}
            >
              {checking ? 'Checking…' : 'Check again'}
            </Button>
          ) : (
            <Button size="sm" variant="outline" leadingIcon={Link} onClick={onConnect}>
              Check {platform} account
            </Button>
          )}
        </div>
      </td>
    </TableRow>
  );
}

/**
 * The account's newest reel: its thumbnail, title and when it went out. When
 * the platform gave us its link, the whole thing opens it in a new tab.
 */
function LastReel({
  reel,
  platform,
}: {
  reel: AnalyticsLastReelDto;
  platform: Account['platform'];
}) {
  const body = (
    <>
      {reel.thumbnailUrl ? (
        <img
          src={reel.thumbnailUrl}
          alt=""
          className="h-[52px] w-[30px] shrink-0 rounded-sm object-cover"
        />
      ) : (
        <span aria-hidden className="bg-bg-muted h-[52px] w-[30px] shrink-0 rounded-sm" />
      )}
      <Stack gap={0.5} className="min-w-0 whitespace-normal">
        <span
          className={cn(
            'text-body-sm-strong text-text-primary',
            reel.url && 'group-hover/reel:underline',
          )}
        >
          {reel.title}
        </span>
        <span className="text-caption text-text-tertiary">
          {reel.url ? 'Posted ' : ''}
          {formatWhen(new Date(reel.scheduledAt))}
        </span>
      </Stack>
    </>
  );
  if (!reel.url) return body;
  return (
    <Inline
      as="a"
      gap={2}
      href={reel.url}
      target="_blank"
      rel="noreferrer"
      aria-label={`${reel.title}, open on ${PLATFORM_NAMES[platform]}`}
      className="group/reel focus-visible:shadow-focus-ring min-w-0 rounded-xs outline-none"
    >
      {body}
    </Inline>
  );
}
